import React, { useState, useEffect, useCallback } from "react";
import "./DemoRegister.css";
import DemoUserService from "../../services/DemoUserService";
import TermsAndConditions from "../legal/TermsAndConditions";
import PrivacyPolicy from "../legal/PrivacyPolicy";

/**
 * Public demo registration page at /demo/register
 * Adaptive multi-step form: register → verify email/phone → pending approval → status
 */
// --- Multi-registration localStorage helpers ---
const STORAGE_KEY = "demoRegistrations";

const getSavedRegistrations = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : {};
  } catch {
    return {};
  }
};

const saveRegistration = (identifier, token) => {
  const regs = getSavedRegistrations();
  regs[identifier] = token;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(regs));
};

const removeRegistration = (identifier) => {
  const regs = getSavedRegistrations();
  delete regs[identifier];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(regs));
};

const DemoRegister = () => {
  // Steps: 'register', 'verify', 'pending', 'approved', 'expired', 'revoked'
  const [step, setStep] = useState("register");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [eventName, setEventName] = useState("");
  const [registrationToken, setRegistrationToken] = useState("");
  const [activeIdentifier, setActiveIdentifier] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [requiresEmail, setRequiresEmail] = useState(false);
  const [requiresPhone, setRequiresPhone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [statusData, setStatusData] = useState(null);
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [showHelpGuide, setShowHelpGuide] = useState(false);
  const [legalView, setLegalView] = useState(null); // null | 'terms' | 'privacy'

  // Theme: 'system' | 'light' | 'dark'
  const [themeMode, setThemeMode] = useState(
    () => localStorage.getItem("demo-theme") || "system",
  );
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia("(prefers-color-scheme: dark)").matches,
  );

  // On mount: migrate legacy token, then load most recent registration
  useEffect(() => {
    // Migrate legacy single-token format
    const legacyToken = localStorage.getItem("demoRegistrationToken");
    if (legacyToken) {
      // Save under a generic key — will be updated once status resolves
      const regs = getSavedRegistrations();
      if (Object.keys(regs).length === 0) {
        saveRegistration("migrated", legacyToken);
      }
      localStorage.removeItem("demoRegistrationToken");
    }

    // Load the most recently saved registration
    const regs = getSavedRegistrations();
    const entries = Object.entries(regs);
    if (entries.length > 0) {
      const [identifier, token] = entries[entries.length - 1];
      setActiveIdentifier(identifier);
      setRegistrationToken(token);
      checkStatus(token, identifier);
    }
  }, []);

  // Listen for OS-level color scheme changes
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setSystemDark(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const switchToRegistration = (identifier) => {
    const regs = getSavedRegistrations();
    const token = regs[identifier];
    if (!token) return;
    setActiveIdentifier(identifier);
    setRegistrationToken(token);
    setError("");
    setSuccessMsg("");
    setEmailCode("");
    setPhoneCode("");
    setShowSwitcher(false);
    checkStatus(token, identifier);
  };

  const checkStatus = useCallback(async (token, identifier) => {
    try {
      const result = await DemoUserService.getStatus(token);
      if (!result.success) {
        // Token invalid, remove this specific registration
        if (identifier) removeRegistration(identifier);
        setRegistrationToken("");
        setActiveIdentifier("");
        return;
      }

      // Update the identifier key to the actual email/phone once we know it
      if (identifier === "migrated" && result.email) {
        removeRegistration("migrated");
        saveRegistration(result.email, token);
        setActiveIdentifier(result.email);
      }
      setStatusData(result);
      setEmailVerified(result.emailVerified);
      setPhoneVerified(result.phoneVerified);
      setRequiresEmail(result.hasEmail);
      setRequiresPhone(result.hasPhone);

      switch (result.status) {
        case "UNVERIFIED":
          setStep("verify");
          break;
        case "PENDING_APPROVAL":
          setStep("pending");
          break;
        case "APPROVED":
          setStep("approved");
          break;
        case "EXPIRED":
          setStep("expired");
          break;
        case "REVOKED":
          setStep("revoked");
          break;
        default:
          setStep("register");
      }
    } catch (err) {
      console.error("Status check failed:", err);
    }
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    if (!email.trim() && !phone.trim()) {
      setError("Please provide at least one: email or phone number.");
      return;
    }

    setLoading(true);
    try {
      const data = {};
      if (firstName.trim()) data.firstName = firstName.trim();
      if (lastName.trim()) data.lastName = lastName.trim();
      if (email.trim()) data.email = email.trim();
      if (phone.trim()) data.phone = phone.trim();
      if (eventName.trim()) data.eventName = eventName.trim();

      const result = await DemoUserService.register(data);

      if (!result.success) {
        setError(result.message || "Registration failed.");
        return;
      }

      setRegistrationToken(result.registrationToken);
      // Save token keyed by identifier (email preferred, else phone)
      const identifier = email.trim() || phone.trim();
      saveRegistration(identifier, result.registrationToken);
      setActiveIdentifier(identifier);
      setRequiresEmail(result.requiresEmailVerification);
      setRequiresPhone(result.requiresPhoneVerification);
      setSuccessMsg(
        "Registration successful! Please verify your contact information.",
      );
      setStep("verify");
    } catch (err) {
      setError("An error occurred. Please try again.");
      console.error("Registration error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    setError("");
    setSuccessMsg("");
    if (!emailCode.trim()) {
      setError("Please enter the 6-digit code.");
      return;
    }
    setLoading(true);
    try {
      const result = await DemoUserService.verifyEmail(
        registrationToken,
        emailCode.trim(),
      );
      if (result.success && (result.verified || result.alreadyVerified)) {
        setEmailVerified(true);
        setSuccessMsg("Email verified!");
        if (result.status === "PENDING_APPROVAL") {
          setStep("pending");
        }
      } else {
        setError(result.message || "Invalid code. Please try again.");
      }
    } catch (err) {
      setError("Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPhone = async () => {
    setError("");
    setSuccessMsg("");
    if (!phoneCode.trim()) {
      setError("Please enter the OTP code.");
      return;
    }
    setLoading(true);
    try {
      const result = await DemoUserService.verifyPhone(
        registrationToken,
        phoneCode.trim(),
      );
      if (result.success && (result.verified || result.alreadyVerified)) {
        setPhoneVerified(true);
        setSuccessMsg("Phone verified!");
        if (result.status === "PENDING_APPROVAL") {
          setStep("pending");
        }
      } else {
        setError(result.message || "Invalid code. Please try again.");
      }
    } catch (err) {
      setError("Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setError("");
    setLoading(true);
    try {
      const result =
        await DemoUserService.resendEmailVerification(registrationToken);
      if (result.success && result.sent) {
        setSuccessMsg("Verification email resent!");
      } else {
        setError(result.message || "Failed to resend. Please try again.");
      }
    } catch (err) {
      setError("Failed to resend verification email.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendPhone = async () => {
    setError("");
    setLoading(true);
    try {
      const result =
        await DemoUserService.resendPhoneVerification(registrationToken);
      if (result.success && result.sent) {
        setSuccessMsg("Verification SMS resent!");
      } else {
        setError(result.message || "Failed to resend. Please try again.");
      }
    } catch (err) {
      setError("Failed to resend verification SMS.");
    } finally {
      setLoading(false);
    }
  };

  const handleStartOver = () => {
    // Keep saved registrations — just reset the form for a new one
    setRegistrationToken("");
    setActiveIdentifier("");
    setStep("register");
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setEventName("");
    setEmailCode("");
    setPhoneCode("");
    setEmailVerified(false);
    setPhoneVerified(false);
    setError("");
    setSuccessMsg("");
    setStatusData(null);
  };

  const effectiveTheme =
    themeMode === "system" ? (systemDark ? "dark" : "light") : themeMode;

  const handleThemeChange = (mode) => {
    setThemeMode(mode);
    localStorage.setItem("demo-theme", mode);
  };

  const renderRegisterForm = () => (
    <form onSubmit={handleRegister} className="demo-form">
      <h2>Register for Demo Access</h2>
      <p className="demo-subtitle">
        Try KinShield's AI-powered scam detection. Forward suspicious emails or
        texts and get instant analysis results.
      </p>

      <div style={{ display: "flex", gap: "12px" }}>
        <div className="demo-field" style={{ flex: 1 }}>
          <label htmlFor="firstName">First Name</label>
          <input
            type="text"
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="John"
            disabled={loading}
          />
        </div>
        <div className="demo-field" style={{ flex: 1 }}>
          <label htmlFor="lastName">Last Name</label>
          <input
            type="text"
            id="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Doe"
            disabled={loading}
          />
        </div>
      </div>

      <div className="demo-field">
        <label htmlFor="email">Email Address (optional)</label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          disabled={loading}
        />
        <small>
          Forward suspicious emails to security@kinprotect.com for analysis
        </small>
      </div>

      <div className="demo-field">
        <label htmlFor="phone">Phone Number (optional)</label>
        <input
          type="tel"
          id="phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+1 (555) 123-4567"
          disabled={loading}
        />
        <small>Forward suspicious texts to our phone number for analysis</small>
      </div>

      <div className="demo-field">
        <label htmlFor="eventName">Event Name (optional)</label>
        <input
          type="text"
          id="eventName"
          value={eventName}
          onChange={(e) => setEventName(e.target.value)}
          placeholder="e.g., CyberSec Conference 2026"
          disabled={loading}
        />
      </div>

      <p className="demo-note">At least one of email or phone is required.</p>

      <button
        type="submit"
        className="demo-btn demo-btn-primary"
        disabled={loading}
      >
        {loading ? "Registering..." : "Register for Demo"}
      </button>

      <p className="demo-register-agreement">
        By registering, you agree to our{" "}
        <button
          type="button"
          className="demo-legal-link"
          onClick={() => setLegalView("terms")}
        >
          <strong>Terms of Use</strong>
        </button>{" "}
        and{" "}
        <button
          type="button"
          className="demo-legal-link"
          onClick={() => setLegalView("privacy")}
        >
          <strong>Privacy Policy</strong>
        </button>
      </p>

      <button
        type="button"
        onClick={() => setShowHelpGuide(true)}
        className="demo-btn demo-btn-secondary"
        style={{ marginTop: "8px" }}
      >
        📖 How to Forward Emails & Texts
      </button>
    </form>
  );

  const renderVerifyStep = () => (
    <div className="demo-form">
      <h2>Verify Your Information</h2>
      <p className="demo-subtitle">
        Please verify the contact information you provided.
      </p>

      {requiresEmail && (
        <div
          className={`demo-verify-section ${emailVerified ? "verified" : ""}`}
        >
          <h3>{emailVerified ? "✅ Email Verified" : "📧 Verify Email"}</h3>
          {!emailVerified ? (
            <>
              <p>Enter the 6-digit code sent to your email.</p>
              <div className="demo-inline-field">
                <input
                  type="text"
                  value={emailCode}
                  onChange={(e) =>
                    setEmailCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  placeholder="000000"
                  maxLength={6}
                  disabled={loading}
                  className="demo-code-input"
                />
                <button
                  onClick={handleVerifyEmail}
                  className="demo-btn demo-btn-primary"
                  disabled={loading || emailCode.length !== 6}
                >
                  {loading ? "..." : "Verify"}
                </button>
              </div>
              <button
                onClick={handleResendEmail}
                className="demo-btn-link"
                disabled={loading}
              >
                Resend code
              </button>
            </>
          ) : (
            <p className="demo-verified-msg">
              Email address verified successfully.
            </p>
          )}
        </div>
      )}

      {requiresPhone && (
        <div
          className={`demo-verify-section ${phoneVerified ? "verified" : ""}`}
        >
          <h3>{phoneVerified ? "✅ Phone Verified" : "📱 Verify Phone"}</h3>
          {!phoneVerified ? (
            <>
              <p>Enter the OTP code sent to your phone.</p>
              <div className="demo-inline-field">
                <input
                  type="text"
                  value={phoneCode}
                  onChange={(e) =>
                    setPhoneCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  placeholder="000000"
                  maxLength={6}
                  disabled={loading}
                  className="demo-code-input"
                />
                <button
                  onClick={handleVerifyPhone}
                  className="demo-btn demo-btn-primary"
                  disabled={loading || phoneCode.length < 4}
                >
                  {loading ? "..." : "Verify"}
                </button>
              </div>
              <button
                onClick={handleResendPhone}
                className="demo-btn-link"
                disabled={loading}
              >
                Resend code
              </button>
            </>
          ) : (
            <p className="demo-verified-msg">
              Phone number verified successfully.
            </p>
          )}
        </div>
      )}
    </div>
  );

  const renderPending = () => (
    <div className="demo-form demo-status-card">
      <div className="demo-status-icon">⏳</div>
      <h2>Pending Admin Approval</h2>
      <p>Your registration has been verified and is awaiting admin approval.</p>
      <p className="demo-note">
        You'll be able to start forwarding suspicious emails and texts once
        approved.
      </p>
      <button
        onClick={() => checkStatus(registrationToken)}
        className="demo-btn demo-btn-secondary"
        disabled={loading}
      >
        {loading ? "Checking..." : "Check Status"}
      </button>
      <button onClick={handleStartOver} className="demo-btn-link">
        Register a different contact
      </button>
    </div>
  );

  const renderApproved = () => (
    <div className="demo-form demo-status-card">
      <div className="demo-status-icon">🎉</div>
      <h2>Demo Access Approved!</h2>
      <p>You can now forward suspicious emails and texts for analysis.</p>
      <div className="demo-instructions">
        {statusData?.hasEmail && (
          <div className="demo-instruction-card">
            <h3>📧 Email Analysis</h3>
            <p>Forward suspicious emails to:</p>
            <code>security@kinprotect.com</code>
            <p className="demo-note">Results will be emailed back to you.</p>
          </div>
        )}
        {statusData?.hasPhone && (
          <div className="demo-instruction-card">
            <h3>📱 Text Analysis</h3>
            <p>Forward suspicious texts to the KinShield number.</p>
            <p className="demo-note">Results will be sent back via SMS.</p>
          </div>
        )}
      </div>
      {statusData && (
        <div className="demo-usage-info">
          <p>
            Usage: <strong>{statusData.requestsUsed || 0}</strong> /{" "}
            <strong>{statusData.maxRequests || 10}</strong> analyses
          </p>
          {statusData.expiresAt && (
            <p>
              Access expires:{" "}
              <strong>{new Date(statusData.expiresAt).toLocaleString()}</strong>
            </p>
          )}
        </div>
      )}
      <button
        onClick={() => checkStatus(registrationToken)}
        className="demo-btn demo-btn-secondary"
      >
        Refresh Status
      </button>
      <div
        style={{
          marginTop: "12px",
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <button
          type="button"
          onClick={() => setShowHelpGuide(true)}
          className="demo-btn-link"
        >
          📖 How to Forward Emails & Texts
        </button>
        <button
          type="button"
          onClick={handleStartOver}
          className="demo-btn-link"
        >
          Want to register another user?
        </button>
      </div>
    </div>
  );

  const renderExpiredOrRevoked = (type) => (
    <div className="demo-form demo-status-card">
      <div className="demo-status-icon">{type === "expired" ? "⏰" : "🚫"}</div>
      <h2>
        {type === "expired" ? "Demo Access Expired" : "Demo Access Revoked"}
      </h2>
      <p>
        {type === "expired"
          ? "Your demo access period has ended. Contact the event organizer if you need extended access."
          : "Your demo access has been revoked. Contact the event organizer for more information."}
      </p>
      <button onClick={handleStartOver} className="demo-btn demo-btn-secondary">
        Register Again
      </button>
    </div>
  );

  return (
    <div className="demo-register-page" data-theme={effectiveTheme}>
      <div
        className="demo-theme-switcher"
        role="group"
        aria-label="Color theme"
      >
        <button
          className={`demo-theme-btn${themeMode === "light" ? " active" : ""}`}
          onClick={() => handleThemeChange("light")}
          title="Light mode"
        >
          ☀️
        </button>
        <button
          className={`demo-theme-btn${themeMode === "dark" ? " active" : ""}`}
          onClick={() => handleThemeChange("dark")}
          title="Dark mode"
        >
          🌙
        </button>
        <button
          className={`demo-theme-btn${themeMode === "system" ? " active" : ""}`}
          onClick={() => handleThemeChange("system")}
          title="System default"
        >
          💻
        </button>
      </div>
      <div className="demo-container">
        <div className="demo-header">
          <img
            src="/KinShieldIcon.png"
            alt="KinShield"
            className="demo-header-icon"
          />
          <div className="demo-header-text">
            <h1>KinShield Demo</h1>
            <p>AI-Powered Scam Detection</p>
          </div>
        </div>

        {error && <div className="demo-alert demo-alert-error">{error}</div>}
        {successMsg && (
          <div className="demo-alert demo-alert-success">{successMsg}</div>
        )}

        {step === "register" && renderRegisterForm()}
        {step === "verify" && renderVerifyStep()}
        {step === "pending" && renderPending()}
        {step === "approved" && renderApproved()}
        {step === "expired" && renderExpiredOrRevoked("expired")}
        {step === "revoked" && renderExpiredOrRevoked("revoked")}

        {/* Account Switcher */}
        {(() => {
          const regs = getSavedRegistrations();
          const regEntries = Object.entries(regs);
          const hasMultiple = regEntries.length > 1;
          const hasSaved = regEntries.length >= 1;
          return (
            ((hasSaved && step === "register") || hasMultiple) && (
              <div className="demo-switcher">
                {step === "register" && hasSaved && (
                  <button
                    onClick={() => setShowSwitcher(!showSwitcher)}
                    className="demo-btn-link demo-switcher-toggle"
                  >
                    {showSwitcher ? "Hide" : "Switch to"} saved registration
                    {regEntries.length > 1 ? "s" : ""} ({regEntries.length})
                  </button>
                )}
                {step !== "register" && hasMultiple && (
                  <button
                    onClick={() => setShowSwitcher(!showSwitcher)}
                    className="demo-btn-link demo-switcher-toggle"
                  >
                    {showSwitcher ? "Hide" : "Switch"} account (
                    {regEntries.length} saved)
                  </button>
                )}
                {showSwitcher && (
                  <div className="demo-switcher-list">
                    {regEntries.map(([id]) => (
                      <button
                        key={id}
                        onClick={() => switchToRegistration(id)}
                        className={`demo-switcher-item ${
                          id === activeIdentifier ? "active" : ""
                        }`}
                        disabled={id === activeIdentifier}
                      >
                        {id === activeIdentifier ? "● " : "○ "}
                        {id}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )
          );
        })()}

        <div className="demo-footer">
          <p>
            Want full access? <a href="/app">Sign up for KinShield</a>
          </p>
        </div>

        {/* Legal Popup (Terms / Privacy) */}
        {legalView && (
          <div
            className="demo-modal-overlay"
            onClick={() => setLegalView(null)}
          >
            <div
              className="demo-legal-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="demo-legal-back-btn"
                onClick={() => setLegalView(null)}
              >
                &#8592; Back to Demo Registration
              </button>
              <div className="demo-legal-content">
                {legalView === "terms" ? (
                  <TermsAndConditions />
                ) : (
                  <PrivacyPolicy />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Legal Popup (Terms / Privacy) */}
        {legalView && (
          <div
            className="demo-modal-overlay"
            onClick={() => setLegalView(null)}
          >
            <div
              className="demo-legal-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="demo-legal-back-btn"
                onClick={() => setLegalView(null)}
              >
                &#8592; Back to Demo Registration
              </button>
              <div className="demo-legal-content">
                {legalView === "terms" ? (
                  <TermsAndConditions />
                ) : (
                  <PrivacyPolicy />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Help Guide Modal */}
        {showHelpGuide && (
          <div
            className="demo-modal-overlay"
            onClick={() => setShowHelpGuide(false)}
          >
            <div className="demo-modal" onClick={(e) => e.stopPropagation()}>
              <div className="demo-modal-header">
                <h2>📖 How to Use KinShield Demo</h2>
                <button
                  onClick={() => setShowHelpGuide(false)}
                  className="demo-modal-close"
                >
                  ✕
                </button>
              </div>
              <div className="demo-modal-body">
                <div className="demo-help-section">
                  <h3>📧 Forward Suspicious Emails</h3>
                  <ol>
                    <li>
                      Open the suspicious email in your email app (Gmail,
                      Outlook, Apple Mail, etc.)
                    </li>
                    <li>
                      Tap or click <strong>Forward</strong> (or the forward
                      arrow icon)
                    </li>
                    <li>
                      In the "To" field, type:{" "}
                      <code>security@kinprotect.com</code>
                    </li>
                    <li>
                      Press <strong>Send</strong>
                    </li>
                    <li>
                      You'll receive an email back with the analysis results
                      within a few minutes
                    </li>
                  </ol>
                  <div className="demo-help-tip">
                    💡 <strong>Tip:</strong> Make sure you forward from the same
                    email address you registered with.
                  </div>
                </div>

                <div className="demo-help-section">
                  <h3>📱 Forward Suspicious Texts</h3>
                  <ol>
                    <li>Open the suspicious text message</li>
                    <li>Long press on the message bubble</li>
                    <li>
                      Select <strong>Forward</strong> (or{" "}
                      <strong>More → Forward</strong>)
                    </li>
                    <li>
                      Send it to the KinShield phone number (provided upon
                      approval)
                    </li>
                    <li>
                      You'll receive a text reply with the analysis results
                    </li>
                  </ol>
                  <div className="demo-help-tip">
                    💡 <strong>Tip:</strong> Forward from the same phone number
                    you registered with.
                  </div>
                </div>

                <div className="demo-help-section">
                  <h3>⚡ What Happens Next?</h3>
                  <p>Our AI-powered system will analyze the content for:</p>
                  <ul>
                    <li>Phishing indicators and social engineering tactics</li>
                    <li>Malicious links and sender reputation</li>
                    <li>Known scam patterns and fraud signatures</li>
                  </ul>
                  <p>
                    Results include a risk verdict, confidence score, specific
                    risk factors, and recommended actions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoRegister;
